import { useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { ClipboardIcon, CheckIcon } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ObservartionCliente from './observation-cliente'
import { formatTel } from '../../../components/form/input/tel-mask-input'
import { formatCEP } from '@/utils/format'
import { formatNome, formatCNPJ } from '@/utils/format'
import { format, parseISO } from 'date-fns'
import { ProcuracaoDialogButton } from '@/features/users/components/update-procuracao'
import { useBankData } from '@/features/client/hooks/use-bank-data'
import ListarClienteOwner from './liste-clientes-owner-form'
import { formatDate } from '@/utils/dates'

interface ClientInfoProps {
  initialClientData: any
}

export default function ClientInfo({ initialClientData }: ClientInfoProps) {
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({})
  const { data: bancos = [] } = useBankData()
  const extraContacts = Array.isArray(initialClientData?.contacts)
    ? initialClientData.contacts.filter((contact: any) => !['OWNER', 'ACCOUNTANT'].includes(contact.type))
    : []
  console.log('initialClientData', initialClientData)
  const isProcuracaoVencidaOuNaoInformada = () => {
    const data = initialClientData?.procuracaoValidaAte
    if (!data) return 'nao-informada'

    // Suporte a formatos DD-MM-AAAA e AAAA-MM-DD
    const [dia, mes, ano] =
      data.includes('-') && data.split('-').length === 3 && data.split('-')[0].length === 4
        ? [data.split('-')[2], data.split('-')[1], data.split('-')[0]]
        : data.split('-')

    const dataValidade = new Date(`${ano}-${mes}-${dia}`)
    const hoje = new Date()

    return dataValidade < hoje ? 'vencida' : null
  }
  const bancoSelecionado = bancos.find(b => String(b.code) === String(initialClientData.banco))
  const bancoDisplay = bancoSelecionado
    ? `${bancoSelecionado.code} - ${bancoSelecionado.name}`
    : initialClientData.banco || 'N/A'

  const handleCopy = (field: string) => {
    setCopiedFields({ ...copiedFields, [field]: true })
    setTimeout(() => {
      setCopiedFields({ ...copiedFields, [field]: false })
    }, 2000)
  }

  const statusProcuracao = isProcuracaoVencidaOuNaoInformada() || ''
  const formatContactType = (type?: string | null) => {
    if (!type) return 'N/A'

    const contactTypeMap: Record<string, string> = {
      signer: 'Assinante',
      owner: 'Proprietário',
      accountant: 'Contador',
      witness: 'Testemunha'
    }

    return contactTypeMap[type.toLowerCase()] ?? type
  }

  const renderField = (label: string, value: string | number | boolean | Date | null | undefined, field: string) => {
    if (value === null || value === undefined) return null
    const displayValue = typeof value === 'boolean' ? (value ? 'Sim' : 'Não') : String(value) // Ensure value is string

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-primary">{label}</label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <span className="flex-grow block w-full rounded-l-md border-input px-3 py-2 bg-background text-primary sm:text-sm">
            {displayValue}
          </span>
          <CopyToClipboard text={displayValue} onCopy={() => handleCopy(field)}>
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 shadow-sm text-sm leading-4 font-medium rounded-r-md text-primary bg-primary-foreground  hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {copiedFields[field] ? (
                <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
              ) : (
                <ClipboardIcon className="h-5 w-5 text-primary" aria-hidden="true" />
              )}
            </button>
          </CopyToClipboard>
        </div>
      </div>
    )
  }

  const renderFieldWithAlert = (label: string, value: string, field: string, alertCondition: boolean | string) => {
    const displayValue = value || 'N/A'
    const isAlert = alertCondition === 'vencida' || alertCondition === 'nao-informada'

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-primary">{label}</label>
        <div className={`mt-1 flex rounded-md shadow-sm ${isAlert ? 'border border-red-500' : ''}`}>
          <span className="flex-grow block w-full rounded-l-md border-input px-3 py-2 bg-background text-primary sm:text-sm">
            {displayValue}
          </span>
          <CopyToClipboard text={displayValue} onCopy={() => handleCopy(field)}>
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 shadow-sm text-sm leading-4 font-medium rounded-r-md text-primary bg-primary-foreground hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {copiedFields[field] ? (
                <CheckIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
              ) : (
                <ClipboardIcon className="h-5 w-5 text-primary" aria-hidden="true" />
              )}
            </button>
          </CopyToClipboard>
        </div>
        {isAlert && (
          <p className="mt-1 text-sm text-red-600 font-semibold">
            {alertCondition === 'vencida'
              ? 'A procuração está vencida.'
              : 'Data de validade da procuração não informada.'}
          </p>
        )}
      </div>
    )
  }

  if (!initialClientData) {
    return <div className="p-4 text-red-500">Nenhum dado do cliente disponível.</div>
  }

  return (
    <div className=" shadow overflow-hidden sm:rounded-lg ">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-primary">Informações do Cliente</h3>

        <p className="mt-1 max-w-2xl text-sm text-primary">
          {initialClientData.nome_gerente && <>Consultor: {formatNome(initialClientData.nome_responsavel)}</>}
        </p>
      </div>
      <Tabs defaultValue="procuracao">
        <TabsList scrollable className="inline-flex h-auto p-2 w-auto min-w-full justify-start gap-2">
          <TabsTrigger value="general">Geral</TabsTrigger>
          <TabsTrigger value="contacts">Contatos</TabsTrigger>
          <TabsTrigger value="financial">Financeiro</TabsTrigger>
          <TabsTrigger value="address">Endereço</TabsTrigger>
          <TabsTrigger value="procuracao">Procuraçao</TabsTrigger>
          <TabsTrigger value="lista-owner">Relacionados</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <div className="px-4 py-6 sm:p-10">
            {renderField('CNPJ/CPF', formatCNPJ(initialClientData.cnpjcpf) || 'N/A', 'cnpjcpf')}
            {renderField('Razão Social', initialClientData.razao_social || 'N/A', 'razaoSocial')}
            {renderField('Cadastrado em:', formatDate(initialClientData.createdDate) || 'N/A', 'createdDate')}
            {renderField('CNAE Principal', initialClientData.cnae_principal || 'N/A', 'cnae_principal')}
            {renderField('Atividade Principal', initialClientData.atividadePrincipal || 'N/A', 'atividadePrincipal')}
          </div>
        </TabsContent>
        <TabsContent value="contacts">
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-lg font-medium mb-4">Responsável</h4>
            {renderField('Nome', formatNome(initialClientData.nomeProprietario) || 'N/A', 'nomeProprietario')}
            {renderField('Contato', formatTel(initialClientData.contatoProprietario) || 'N/A', 'contatoProprietario')}
            {renderField('E-mail', initialClientData.emailProprietario || 'N/A', 'emailProprietario')}
            <h4 className="text-lg font-medium mb-4 mt-6">Contador</h4>
            {renderField('Nome', formatNome(initialClientData.nomeContador) || 'N/A', 'nomeContador')}
            {renderField('Contato', formatTel(initialClientData.contatoContador) || 'N/A', 'contatoContador')}
            {renderField('E-mail', initialClientData.emailContador || 'N/A', 'emailcontador')}
            {extraContacts.length > 0 && (
              <>
                <h4 className="text-lg font-medium mb-4 mt-6">Contatos adicionais</h4>
                {extraContacts.map((contact: any, index: number) => (
                  <div key={contact.id || `${contact.type}-${index}`} className="mb-6 rounded-lg border p-4">
                    {renderField('Tipo', formatContactType(contact.type), `contact-type-${index}`)}
                    {renderField('Nome', formatNome(contact.nome) || 'N/A', `contact-name-${index}`)}
                    {renderField(
                      'Contato',
                      contact.contato ? formatTel(contact.contato) : 'N/A',
                      `contact-phone-${index}`
                    )}
                    {renderField('E-mail', contact.email || 'N/A', `contact-email-${index}`)}
                    {renderField('Documento', contact.document || 'N/A', `contact-document-${index}`)}
                  </div>
                ))}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="financial">
          <div className="px-4 py-5 sm:p-6">
            {renderField('Conta', initialClientData.conta || 'N/A', 'conta')}
            {renderField('Agência', initialClientData.agencia || 'N/A', 'agencia')}
            {renderField('Banco', bancoDisplay, 'banco')}
            {renderField('Honorários', initialClientData.comissaoTenant, 'honorarios')}
          </div>
        </TabsContent>
        <TabsContent value="address">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* campos “pequenos” lado a lado */}
              <div>{renderField('CEP', formatCEP(initialClientData.cep) || 'N/A', 'cep')}</div>

              {/* logradouro ocupa 2 colunas no sm e 3 no lg */}
              <div className="sm:col-span-2 ">
                {renderField('Logradouro', initialClientData.logradouro || 'N/A', 'logradouro')}
              </div>
              <div>{renderField('Número', initialClientData.numero || 'N/A', 'numero')}</div>

              {/* restante distribui; ajuste spans como preferir */}
              <div>{renderField('Bairro', initialClientData.bairro || 'N/A', 'bairro')}</div>
              <div>{renderField('Município', initialClientData.municipio || 'N/A', 'municipio')}</div>
              <div>{renderField('Estado', initialClientData.estado || 'N/A', 'estado')}</div>
              <div className="sm:col-span-2">
                {renderField('Complemento', initialClientData.complemento || 'N/A', 'complemento')}
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="procuracao">
          <div className="px-4 py-5 sm:p-6">
            {renderField(
              'Procuração Habilitada',
              initialClientData.procuracaoHabilitada || 'N/A',
              'procuracaoHabilitada'
            )}
            {renderField(
              'Habilitada em',
              initialClientData.procuracaoHabilitadaEm
                ? format(parseISO(initialClientData.procuracaoHabilitadaEm), 'dd/MM/yyyy')
                : 'N/A',
              'procuracaoHabilitadaEm'
            )}
            {renderFieldWithAlert(
              'Válida até',
              initialClientData.procuracaoValidaAte
                ? format(parseISO(initialClientData.procuracaoValidaAte), 'dd/MM/yyyy')
                : 'N/A',
              'procuracaoValidaAte',
              statusProcuracao
            )}
          </div>
          <div className="flex justify-end mb-2 mr-2">
            <ProcuracaoDialogButton clientId={initialClientData.cnpjcpf || ''} />
          </div>
        </TabsContent>
        <TabsContent className="md:h-[360px]" value="lista-owner">
          {initialClientData.id_owner ? (
            <ListarClienteOwner id_owner={initialClientData.id_owner} id_client={initialClientData.cnpjcpf} />
          ) : (
            <div className="p-4 text-yellow-600">Sem proprietário vinculado a este cliente.</div>
          )}
        </TabsContent>
      </Tabs>

      <ObservartionCliente type={'GERAL'} cnpj={initialClientData.cnpjcpf} />
    </div>
  )
}
